import { Transition } from '@headlessui/react';
import { Fragment, useState, useRef, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import Upload from '@components/shared/upload/upload.tsx';
import { DocumentTypeOptions } from '@utils/types/editors/utils.ts';
import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import {
  FileText,
  Plus,
  Subtitles,
  AlignHorizontalJustifyCenter,
  Radio
} from 'lucide-react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useEntitlements } from '@utils/providers/entitlements-context.tsx';
import { useDragToUpload } from '@utils/hooks/use-drag-to-upload.ts';
import { DropToUploadHint } from './drop-to-upload-hint.tsx';
import { PARAMS, PATHS } from '../../../router/paths.ts';

export default function UploadButton() {
  const [type, setType] = useState<DocumentTypeOptions | 'align' | ''>('');
  const [isOpen, setIsOpen] = useState(false);
  const [initialFiles, setInitialFiles] = useState<File[]>([]);
  const [initialFolderId, setInitialFolderId] = useState<number>();
  const { t } = useTranslation();
  const { getColour, getBrandTint } = useThemeContext();
  const navigate = useNavigate();
  // A toolbar slot may render outside the folder Route's params context.
  const folderRoute = useMatch('/folders/:folderId');
  // A custom model page takes dropped files itself, for its dataset dialog. If
  // this button claimed them too, the drop would open the transcript/subtitle
  // upload instead.
  const customModelRoute = useMatch(PATHS(PARAMS.MODEL_ID).CUSTOM_MODEL);

  // Theme-aware colors
  const themeText = getColour('normalText');
  const themeLayer = getColour('secondLayer');
  const themeBorder = getColour('border');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { hasFeature, uploadSuspended } = useEntitlements();

  const menuItems = useMemo(
    () =>
      [
        // Upload to transcribe (batch) or to translate (translation-only plans)
        (hasFeature('batch') || hasFeature('translation')) && {
          icon: FileText,
          label: t('transcript'),
          onClick: () => {
            setType('document');
            setIsOpen(false);
          },
          disabled: false
        },
        // Upload to transcribe (batch) or to translate (translation-only plans)
        (hasFeature('batch') || hasFeature('translation')) && {
          icon: Subtitles,
          label: t('subtitles'),
          onClick: () => {
            setType('caption');
            setIsOpen(false);
          },
          disabled: false
        },
        // Alignment feature
        hasFeature('alignment') && {
          icon: AlignHorizontalJustifyCenter,
          label: t('force-alignment'),
          onClick: () => {
            setType('align');
            setIsOpen(false);
          },
          disabled: false
        },
        // Realtime feature
        hasFeature('realtime') && {
          icon: Radio,
          label: t('realtime-editor'),
          onClick: () => {
            navigate('/realtime');
            setIsOpen(false);
          },
          disabled: false
        }
      ].filter(Boolean) as {
        icon: typeof FileText;
        label: string;
        onClick: () => void;
        disabled: boolean;
      }[],
    [hasFeature, t, navigate]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const { isDragging, droppedFiles, droppedFolderId, clearDroppedFiles } =
    useDragToUpload(
      type === '' &&
        !uploadSuspended &&
        !customModelRoute &&
        menuItems.some(item => !item.disabled)
    );

  useEffect(() => {
    if (!droppedFiles.length) return;
    setInitialFiles(droppedFiles);
    setInitialFolderId(
      droppedFolderId ??
        (folderRoute ? Number(folderRoute.params.folderId) : undefined)
    );
    clearDroppedFiles();
    setType('document');
  }, [droppedFiles, droppedFolderId]);

  // Close the dropdown if entitlements load in and leave it with no items
  useEffect(() => {
    if (menuItems.length === 0) setIsOpen(false);
  }, [menuItems.length]);

  const handleSetType = (newType: DocumentTypeOptions | 'align' | '') => {
    setType(newType);
    if (newType === '') {
      setInitialFiles([]);
      setInitialFolderId(undefined);
    }
  };

  return (
    <>
      {isDragging && type === '' && <DropToUploadHint />}
      <div className="relative" ref={dropdownRef}>
        <Button
          size="medium"
          variant="primary"
          className="w-full"
          onClick={() => setIsOpen(!isOpen)}
          icon={<Plus size={15} />}
          label={t('create')}
          disabled={menuItems.length === 0 || uploadSuspended}
          title={uploadSuspended ? t('uploading-suspended-tooltip') : undefined}
        />

        <Transition
          show={isOpen && menuItems.length > 0}
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <div
            className="absolute py-1 z-50 mt-2 w-56 origin-top-right rounded-2xl shadow-pop border focus:outline-none"
            style={{
              backgroundColor: themeLayer,
              borderColor: themeBorder
            }}
          >
            {menuItems.map((item, index) => (
              <button
                key={index}
                className={classNames(
                  item.disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'transition-colors',
                  'block px-4 py-2 text-sm w-full text-left'
                )}
                style={{
                  color: themeText
                }}
                onMouseEnter={e => {
                  if (!item.disabled) {
                    e.currentTarget.style.backgroundColor = getBrandTint(0.04);
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={!item.disabled ? item.onClick : undefined}
                disabled={item.disabled}
              >
                <span className={'flex gap-2'}>
                  <item.icon width={17} />
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </Transition>
      </div>
      {type !== '' && (
        <Upload
          type={type}
          setType={handleSetType}
          initialFiles={initialFiles}
          initialFolderId={initialFolderId}
        />
      )}
    </>
  );
}
